// src/components/common/Layout/MainLayout.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Collapse,
  Chip,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  CreditCard,
  AccountBalance,
  Person,
  Logout,
  Security,
  Receipt,
  Analytics,
  Support,
  ExpandLess,
  ExpandMore,
  PersonAdd,
  Business,
  Settings,
  Key,
  Warning,
  TrendingUp,
  LocalOffer,
  ReceiptLong,
  NoteAdd,
  AddCard,
  PendingActions,
  CloudUpload,
  Description,
  Book,
  Email,
  Phone,
  LocationOn,
  Close,
  Edit,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthService from '../../../services/authService';
import useRoleColors from '../../../hooks/useRoleColors';
import { BRAND_CONFIG } from '../../../config/brandConfig';
import { useCompany } from '../../../contexts/CompanyContext';
import { Autocomplete, TextField, InputAdornment, CircularProgress } from '@mui/material';

const drawerWidth = 280;

// Navigation items based on user role
const getNavigationItems = (role) => {
  // Navigation items configuration
  const TEMP_HIDE_OTHER_MENUS = true;
  
  const commonItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/edefter/dashboard',
      roles: ['Admin', 'User'],
      hidden: TEMP_HIDE_OTHER_MENUS, // Temporarily hide Dashboard
    },
  ];

  const roleBasedItems = {
    'Admin': [     
      { 
        text: 'Dashboard', 
        icon: <Dashboard />, 
        path: '/edefter/dashboard',
        hidden: false,
      },
      { 
        text: 'Add Company', 
        icon: <Business />, 
        path: '/edefter/add-company',
        hidden: false,
      },
      { 
        text: 'General Journal', 
        icon: <Book />, 
        path: '/edefter/general-journal',
        hidden: false,
      },
      { 
        text: 'Trial Balance', 
        icon: <Description />, 
        path: '/edefter/trial-balance',
        hidden: false,
      }
    ],
    'User': [
      {text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/edefter/dashboard',
      hidden: false}
    ]
  };

  // Filter out hidden items
  const filteredCommonItems = commonItems.filter(item => !item.hidden);
  const filteredRoleItems = (roleBasedItems[role] || []).filter(item => !item.hidden);

  return [
    ...filteredCommonItems,
    ...filteredRoleItems,
  ];
};

const MainLayout = ({ children, user = { name: 'John Doe', role: 'User', email: 'john@example.com' } }) => {
  const theme = useTheme();
  const {
    accent: accentColor,
    accentLight,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    contrast: accentContrast,
  } = useRoleColors();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompany, setSelectedCompany, companies, loadingCompanies } = useCompany();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [companyMenuAnchorEl, setCompanyMenuAnchorEl] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCompanyMenuOpen = (event) => {
    setCompanyMenuAnchorEl(event.currentTarget);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchorEl(null);
  };

  const handleSelectAnotherCompany = () => {
    setSelectedCompany(null);
    handleCompanyMenuClose();
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
    handleProfileMenuClose();
  };

  const handleExpandClick = (itemText) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };


  const SidebarHeader = () => {
    const headerColor = 'rgba(16, 38, 166)';
    const headerColorMid = 'rgba(14, 33, 148)';
    const headerColorDark = 'rgba(12, 28, 130)';
    const headerGradient = `linear-gradient(135deg, ${headerColor} 0%, ${headerColorMid} 50%, ${headerColorDark} 100%)`;
    const headerGradientHover = `linear-gradient(135deg, rgba(20, 45, 180, 1) 0%, rgba(18, 40, 170, 1) 50%, ${headerColor} 100%)`;
    
    return (
      <Box 
        onClick={() => handleNavigation('/edefter/dashboard')}
        sx={{ 
          p: 1.5, 
          background: headerGradient,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: headerGradientHover,
            transform: 'scale(1.02)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
          }
        }}
      >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 65,
            overflow: 'hidden'
          }}>
            <img 
              src={BRAND_CONFIG.logo} 
              alt={BRAND_CONFIG.name}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain'
              }}
              onError={(e) => {
                // Fallback to icon if image fails to load
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
              }}
            />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.95, fontSize: '0.7rem',fontWeight: 600 }}>
              {BRAND_CONFIG.tagline}
            </Typography>
          </Box>
        </Box>
      </Box>
      </Box>
    );
  };

  const NavigationList = () => (
    <List sx={{ px: 2, py: 2 }}>
      {getNavigationItems(user.role).map((item, index) => {
       
       
        
        return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isActiveRoute(item.path)}
              onClick={() => {
                if (item.subItems) {
                  handleExpandClick(item.text);
                }
                  if (item.path) {
                    handleNavigation(item.path);
                  }
              }}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                px: 2,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(20, 45, 180, 1) 0%, rgba(18, 40, 170, 1) 50%, rgba(16, 38, 166) 100%)',
                  }
                },
                '&:hover': {
                  background: softBg,
                  transform: 'translateX(4px)',
                }
              }}
            >
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: 500 
                }}
              />
              {item.subItems && (
                expandedItems[item.text] ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
          </ListItem>
          
          {item.subItems && (
            <Collapse in={expandedItems[item.text]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.subItems.map((subItem) => (
                  <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isActiveRoute(subItem.path)}
                      onClick={() => handleNavigation(subItem.path)}
                      sx={{ 
                        pl: 7,
                        pr: 2,
                        borderRadius: 2,
                        minHeight: 40,
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          background: softBg,
                          color: accentColor,
                          fontWeight: 600,
                          borderLeft: '3px solid',
                          borderColor: accentColor,
                          '&:hover': {
                            background: softBgHover,
                          }
                        },
                        '&:hover': {
                          background: softBgHover,
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <ListItemText 
                        primary={subItem.text}
                        primaryTypographyProps={{ 
                          fontSize: '0.85rem',
                          fontWeight: isActiveRoute(subItem.path) ? 600 : 400
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
        );
      })}
    </List>
  );

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: '#ffffff'
    }}>
      <SidebarHeader />
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <NavigationList />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              background: softBg,
              '&:hover': {
                background: softBgHover,
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {selectedCompany ? (
              // Company Information Display - Paper Card Style (like Profile Menu)
              <Paper
                elevation={0}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  cursor: 'pointer',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  maxWidth: 'fit-content',
                  '&:hover': { 
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-1px)'
                  }
                }}
                onClick={handleCompanyMenuOpen}
              >
                <Avatar sx={{ 
                  width: { xs: 32, sm: 36 }, 
                  height: { xs: 32, sm: 36 },
                  background: accentGradient,
                  border: '2px solid',
                  borderColor: accentColor,
                  flexShrink: 0,
                }}>
                  <Business sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1, display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                    {selectedCompany.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                    {selectedCompany.vergiKimlikNo || 'Company'}
                  </Typography>
                </Box>
                <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
              </Paper>
            ) : (
              // Company Selector - Show when no company is selected (replaces page title)
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                value={selectedCompany}
                onChange={(event, newValue) => {
                  setSelectedCompany(newValue);
                }}
                loading={loadingCompanies}
                size="small"
                noOptionsText={companies.length === 0 && !loadingCompanies ? "No companies found. Add a company first." : "No options"}
                sx={{ 
                  minWidth: { xs: 200, sm: 280, md: 320 },
                  width: { xs: '100%', sm: 280, md: 320 },
                  maxWidth: { xs: '100%', md: 450 },
                  '& .MuiAutocomplete-inputRoot': {
                    paddingRight: '40px !important',
                    paddingLeft: '12px !important',
                  },
                  '& .MuiInputBase-input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingRight: '8px !important',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '12px !important',
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Company"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: false,
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Business fontSize="small" color="disabled" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                  <Box component="li" key={key} {...optionProps}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    {option.vergiKimlikNo && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                        ({option.vergiKimlikNo})
                      </Typography>
                    )}
                  </Box>
                  );
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Company Info - Show when company is selected */}
            {selectedCompany && (
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                alignItems: 'center', 
                gap: 2,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid',
                borderColor: 'divider',
              }}>
                {selectedCompany.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {selectedCompany.phone}
                    </Typography>
                  </Box>
                )}
                {selectedCompany.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {selectedCompany.email}
                    </Typography>
                  </Box>
                )}
                {selectedCompany.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedCompany.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Paper
              elevation={0}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'pointer',
                px: 1.5,
                py: 1,
                borderRadius: 2,
                borderColor: 'divider',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  borderColor: accentColor,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
              onClick={handleProfileMenuOpen}
            >
              <Avatar sx={{ 
                width: 36, 
                height: 36,
                background: accentGradient,
                fontSize: '0.9rem',
                fontWeight: 600
              }}>
                {user.name && user.surname ? 
                  `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                  user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                  {user.name && user.surname ? `${user.name} ${user.surname}` : user.name || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                  {user.role || 'User'}
                </Typography>
              </Box>
              <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
            </Paper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Company Menu */}
      <Menu
        anchorEl={companyMenuAnchorEl}
        open={Boolean(companyMenuAnchorEl)}
        onClose={handleCompanyMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        
        <MenuItem 
          onClick={handleSelectAnotherCompany}
          sx={{ 
            py: 1.5,
            '&:hover': {
              background: softBg,
            }
          }}
        >
          <ListItemIcon><Business fontSize="small" color="primary" /></ListItemIcon>
          <Typography variant="body2">Select another company</Typography>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {user.name && user.surname ? `${user.name} ${user.surname}` : user.name || 'User'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {user.email}
          </Typography>
        </Box>
        
        <MenuItem 
          onClick={() => handleNavigation('/profile')}
          sx={{ 
            py: 1.5,
            '&:hover': {
              background: softBg,
            }
          }}
        >
          <ListItemIcon><Person fontSize="small" color="primary" /></ListItemIcon>
          <Typography variant="body2">Profile Settings</Typography>
        </MenuItem>
        
        <MenuItem 
          onClick={() => handleNavigation('/change-password')}
          sx={{ 
            py: 1.5,
            '&:hover': {
              background: softBg,
            }
          }}
        >
          <ListItemIcon><Key fontSize="small" color="primary" /></ListItemIcon>
          <Typography variant="body2">Change Password</Typography>
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem 
          onClick={() => {
            handleProfileMenuClose();
            // Call logout immediately - it will redirect
            AuthService.logout(true);
          }}
          sx={{ 
            py: 1.5,
            color: 'error.main',
            '&:hover': {
              background: 'rgba(244, 67, 54, 0.08)',
            }
          }}
        >
          <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              boxShadow: '4px 0 24px rgba(0,0,0,0.04)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f5f7fa',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }} />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;